import type {
  ActionDto,
  GetIssueResponseDto,
  IssueFilteringDtoOptions,
  IssueSubtypeDto,
  IssueTypeDto,
  MinimalIssueSubtypeDto,
  MinimalIssueTypeDto,
  UserDto,
} from '@issuemanager/api-client';
import type {
  BaseFilterModelType,
  GroupedEntityTitleDto,
  GroupedStatus,
  GroupedUserDto,
  TransformedIssueActionCountDto,
  TransformedIssueEntityNameDto,
  TransformedIssueSLAsDto,
} from '@issuemanager/apps/client/components';
import {
  ActionStatus,
  ISSUE_FIELD_ACTION_CREATED,
  ISSUE_FIELD_ACTION_DUE_DATE,
  ISSUE_FIELD_CREATED,
  ISSUE_FIELD_DUE_DATE,
  ISSUE_FIELD_UPDATED,
  IssueActionProgressEnum,
  IssueStatusEnum,
} from '@issuemanager/constants';
import { IssueActionProgressData } from '../issue-action-progress-label';
import { IssueSeverityData } from '../issue-severity-chip';
import { IssueSourceApplicationData } from '../issue-source-app';

export interface IssueGridFilterModel extends BaseFilterModelType {
  // common columns
  owner?: GroupedUserDto[];
  actionOwner?: GroupedUserDto[];
  approver?: GroupedUserDto[];
  actionApprover?: GroupedUserDto[];
  dueDate?: [Date | null, Date | null];
  actionDueDate?: [Date | null, Date | null];
  createdAt?: [Date | null, Date | null];
  actionCreatedAt?: [Date | null, Date | null];
  updatedAt?: [Date | null, Date | null];
  actionUpdatedAt?: [Date | null, Date | null];
  // only issue columns
  code?: GetIssueResponseDto[];
  title?: GroupedEntityTitleDto[];
  status?: GroupedStatus[];
  sourceApplication?: IssueSourceApplicationData[];
  sourceEntityName?: TransformedIssueEntityNameDto[];
  actionStatus?: GroupedStatus[];
  issueType?: IssueTypeDto[];
  issueSubtype?: IssueSubtypeDto[];
  severity?: IssueSeverityData[];
  creator?: UserDto[];
  manager?: UserDto[];
  slaDueDays?: TransformedIssueSLAsDto[];
  actionCount?: TransformedIssueActionCountDto[];
  actionProgress?: IssueActionProgressData[];
  actionTitle?: GroupedEntityTitleDto[];
}

export type MasterIssueRowModel = {
  id: string;
  // table grouping index
  path: string[];
  // common columns
  status: IssueStatusEnum | ActionStatus;
  createdAt: string;
  dueDate: Date | null;
  owner: ActionDto['owner'];
  approver?: ActionDto['approver'];
  code: string;
  // only issue columns
  issueType?: MinimalIssueTypeDto;
  issueSubtype?: MinimalIssueSubtypeDto | null;
  severity?: IssueSeverityData;
  creator?: UserDto;
  manager?: UserDto;
  updatedAt?: string;
  actions?: ActionDto[];
  actionProgress?: IssueActionProgressEnum;
};

export type ExtendedIssueFilteringDtoOptions = IssueFilteringDtoOptions & {
  actionStatus?: string | string[];
  actionOwner?: string | string[];
  actionApprover?: string | string[];
  actionDueDateRangeStart?: string;
  actionDueDateRangeEnd?: string;
  actionCreatedAtRangeStart?: string;
  actionCreatedAtRangeEnd?: string;
};

type DateFieldKeys =
  | typeof ISSUE_FIELD_CREATED
  | typeof ISSUE_FIELD_UPDATED
  | typeof ISSUE_FIELD_ACTION_DUE_DATE
  | typeof ISSUE_FIELD_ACTION_CREATED
  | typeof ISSUE_FIELD_DUE_DATE;

export type IssueFilterDateFieldMap = {
  [key in DateFieldKeys]: [
    keyof ExtendedIssueFilteringDtoOptions,
    keyof ExtendedIssueFilteringDtoOptions,
  ];
};

/**
 * @description this is needed to avoid typescript error type:
 * "Expression produces a union type that is too complex to represent"
 * Since type ExtendedIssueFilteringDtoOptions seems like it's too complex for the mighty TypeScript
 */
export function assignFilterParam<
  K extends keyof ExtendedIssueFilteringDtoOptions,
>(
  target: ExtendedIssueFilteringDtoOptions,
  key: K,
  value: ExtendedIssueFilteringDtoOptions[K],
) {
  // eslint-disable-next-line no-param-reassign
  target[key] = value;
}
